import { useLocation, useNavigate } from 'react-router-dom';
import { Settings, User, Home, X, LogOut, MessageCircle } from 'lucide-react';
import { useAuth } from '../stores/supabaseAuthStore';
import { ROUTES } from '../routes';
import OpenSelfLogo from '@/assets/openself-logo.png';

interface SidebarProps {
  isMobile?: boolean;
  onClose?: () => void;
}

function Sidebar({ isMobile = false, onClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  // Only compact in chat view on desktop
  const isCompact = location.pathname.startsWith('/chat') && !isMobile;

  const isActive = (path: string) => {
    if (path === ROUTES.HOME) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile && onClose) {
      onClose();
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate(ROUTES.LOGIN);
  };

  const menuItems = [
    {
      icon: Home,
      label: 'Home',
      active: isActive(ROUTES.HOME),
      onClick: () => handleNavigation(ROUTES.HOME),
    },
    {
      icon: MessageCircle,
      label: 'Chat',
      active: isActive(ROUTES.CHAT),
      onClick: () => handleNavigation(ROUTES.CHAT),
    },
    {
      icon: User,
      label: 'Profile',
      active: isActive(ROUTES.PROFILE),
      onClick: () => handleNavigation(ROUTES.PROFILE),
    },
    {
      icon: Settings,
      label: 'Settings',
      active: isActive(ROUTES.SETTINGS),
      onClick: () => handleNavigation(ROUTES.SETTINGS),
    },
  ];

  // Get user display info from Supabase user metadata
  const userDisplayName = user?.user_metadata?.name || user?.user_metadata?.full_name || 'User';
  const userAvatarUrl = user?.user_metadata?.avatar_url || user?.user_metadata?.picture;
  const userEmail = user?.email || '';

  return (
    <div
      className={`bg-kind-graphite border-r border-gray-300 dark:border-white/10 flex flex-col h-full ${
        isCompact ? 'w-20' : 'w-64'
      } transition-all duration-300 ${isMobile ? 'fixed inset-y-0 left-0 z-50 shadow-lg' : ''}`}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-300 dark:border-white/10 flex items-center justify-center">
        {!isCompact && (
          <div className="flex items-center justify-center space-x-3">
            <img src={OpenSelfLogo} alt="OpenSelf Logo" className="w-8 h-8" />
            <span className="font-title font-medium text-xl text-kind-text-primary">OpenSelf</span>
          </div>
        )}

        {isCompact && (
          <div className="flex items-center justify-center mx-auto">
            <img src={OpenSelfLogo} alt="OpenSelf Logo" className="w-6 h-6" />
          </div>
        )}

        {isMobile && (
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-kind-container transition-colors"
          >
            <X className="h-5 w-5 text-kind-text-secondary" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2">
        {menuItems.map((item, index) => (
          <button
            key={index}
            onClick={item.onClick}
            className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg transition-colors text-left ${
              item.active
                ? 'bg-kind-purple text-white'
                : 'text-kind-text-secondary hover:text-kind-text-primary hover:bg-kind-container'
            }`}
            title={isCompact ? item.label : undefined}
          >
            <item.icon
              className={`h-5 w-5 flex-shrink-0 ${
                item.active ? 'text-white' : 'text-kind-text-secondary'
              }`}
            />
            {!isCompact && (
              <div className="flex-1">
                <span className="font-body font-medium">{item.label}</span>
              </div>
            )}
          </button>
        ))}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-gray-300 dark:border-white/10">
        {!isCompact && user && (
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-8 bg-kind-container rounded-full flex items-center justify-center">
              {userAvatarUrl ? (
                <img src={userAvatarUrl} alt={userDisplayName} className="w-8 h-8 rounded-full" />
              ) : (
                <User className="h-4 w-4 text-kind-text-secondary" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-body font-medium text-kind-text-primary truncate">
                {userDisplayName}
              </p>
              <p className="text-xs font-body font-light text-kind-text-secondary truncate">
                {userEmail}
              </p>
            </div>
          </div>
        )}

        <button
          onClick={handleLogout}
          className={`w-full flex items-center space-x-3 px-3 py-2 text-kind-text-secondary hover:text-kind-text-primary hover:bg-kind-container rounded-lg transition-colors ${
            isCompact ? 'justify-center' : ''
          }`}
          title={isCompact ? 'Sign out' : undefined}
        >
          <LogOut className="h-5 w-5 text-kind-text-secondary" />
          {!isCompact && <span>Sign out</span>}
        </button>
      </div>
    </div>
  );
}

export { Sidebar };
